/**
 * Central scroll management for CPDForge.
 *
 * Architecture:
 *   Public pages (Landing, Pricing, etc.) use window scroll.
 *   App pages (inside AppLayout) use the main-content-area div as their scroll container.
 *   CourseView has its own nested <main> scroll container.
 *
 * This utility detects the correct container and scrolls it.
 */

/**
 * Scroll the correct container to the top.
 * Checks for AppLayout's main-content-area first, falls back to window.
 * @param {'smooth'|'instant'} [behavior='smooth']
 */
export function scrollToTop(behavior = 'smooth') {
  const appContainer = document.querySelector('[data-testid="main-content-area"]');
  if (appContainer) {
    appContainer.scrollTo({ top: 0, behavior });
  }
  window.scrollTo({ top: 0, behavior });
}

/**
 * Scroll a specific element (ref) to the top and optionally move focus to it.
 * Used when a component owns its own scroll container (e.g. CourseView <main>).
 * @param {React.RefObject} ref
 * @param {'smooth'|'instant'} [behavior='smooth']
 */
export function scrollRefToTop(ref, behavior = 'smooth') {
  if (ref?.current) {
    ref.current.scrollTo({ top: 0, behavior });
    ref.current.focus({ preventScroll: true });
  }
}

/**
 * Lock body scroll (for modals).
 * Saves the current scroll position and prevents background scrolling.
 */
export function lockScroll() {
  const scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
}

/**
 * Unlock body scroll (when modal closes).
 * Restores the previous scroll position.
 */
export function unlockScroll() {
  const scrollY = document.body.style.top;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';
  window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
}
